'use client';

import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import cn from '@/helpers/cn';

import css from './select.module.scss';

type Option = { label: string; value: string };

interface Props {
	name: string;
	options: Option[];
	value?: Option | null;
	placeholder?: string;
	onChange?: (value: string) => void;
}

const getPortalContainer = (anchor: HTMLElement | null) =>
	anchor?.closest<HTMLElement>('[role="dialog"]') ?? document.body;

const getMenuPosition = (anchor: HTMLElement, container: HTMLElement): React.CSSProperties => {
	const rect = anchor.getBoundingClientRect();

	if (container === document.body)
		return { position: 'fixed', top: rect.bottom, left: rect.left, width: rect.width };

	const containerRect = container.getBoundingClientRect();
	return {
		position: 'absolute',
		top: rect.bottom - containerRect.top + container.scrollTop,
		left: rect.left - containerRect.left + container.scrollLeft,
		width: rect.width
	};
};

const CustomDropdown: FC<Props> = ({
	options,
	value,
	placeholder = 'How did you find Qream?',
	onChange
}) => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLUListElement>(null);

	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
	const [isOpen, setIsOpen] = useState(false);

	const selected = options.find(opt => opt.value === value?.value);

	useEffect(() => {
		if (!isOpen || !buttonRef.current) return;

		const container = getPortalContainer(buttonRef.current);
		setPortalContainer(container);
		setMenuStyle(getMenuPosition(buttonRef.current, container));

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
			setIsOpen(false);
		};

		const handleScroll = (event: Event) => {
			if (menuRef.current?.contains(event.target as Node)) return;
			setIsOpen(false);
		};

		const handleClose = () => setIsOpen(false);

		document.addEventListener('mousedown', handleClickOutside);
		window.addEventListener('scroll', handleScroll, true);
		window.addEventListener('resize', handleClose);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			window.removeEventListener('scroll', handleScroll, true);
			window.removeEventListener('resize', handleClose);
		};
	}, [isOpen]);

	const menu = isOpen && portalContainer && (
		<ul ref={menuRef} className={css.dropdownMenu} style={menuStyle}>
			{options.map(option => (
				<li
					key={option.value}
					role="presentation"
					className={cn(css.dropdownItem, value?.value === option.value ? css.selected : '')}
					onClick={() => {
						onChange?.(option.value);
						setIsOpen(false);
					}}
				>
					{option.label}
				</li>
			))}
		</ul>
	);

	return (
		<div className={css.dropdown}>
			<button
				ref={buttonRef}
				type="button"
				className={cn(css.dropdownButton, selected ? css.selected : '')}
				onClick={() => setIsOpen(prev => !prev)}
			>
				<span>{selected?.label ?? placeholder}</span>
				<div />

				<Image
					width={16}
					height={16}
					src="/assets/icons/arrow.svg"
					alt="arrow icon"
					className={cn(css.arrow, isOpen ? css.arrowOpen : '')}
				/>
			</button>

			{menu ? createPortal(menu, portalContainer) : null}
		</div>
	);
};

export default CustomDropdown;
